// Bottom-sheet modal for moving selected media into a subfolder.
// Lets the user either pick an existing folder OR type a brand-new one.
// Renders nothing when `open` is false.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Folder, FolderPlus, X as XIcon, Check, ArrowLeft } from "lucide-react";

export default function MoveToFolderModal({
  open,
  onClose,
  onMove,           // (subfolder_id | null, new_name | null) => Promise<void>
  subfolders = [],
  count = 0,
  isHost = false,
  myGuestId = null, // used to grey-out folders that aren't ours (guests only)
}) {
  const [step, setStep] = useState("pick");        // 'pick' | 'create'
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep("pick");
    setNewName("");
    setBusy(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (step === "create") {
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [step]);

  const visibleFolders = useMemo(() => {
    // Guests only see folders they created OR legacy ones with no owner.
    if (isHost) return subfolders;
    return subfolders.filter(
      (s) => !s.creator_guest_id || s.creator_guest_id === myGuestId
    );
  }, [subfolders, isHost, myGuestId]);

  if (!open) return null;

  const doMove = async (subfolderId, name) => {
    if (busy) return;
    setBusy(true);
    try {
      await onMove(subfolderId, name);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-6"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="move-folder-modal"
    >
      <div
        className="glass-card w-full max-w-md p-5 sm:p-6 relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: "0 30px 80px -20px rgba(127,75,255,0.45)" }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 rounded-full p-2 transition hover:bg-white/10"
          aria-label="Close"
          data-testid="move-folder-close"
        >
          <XIcon size={16} color="rgba(255,255,255,0.75)" />
        </button>

        {step === "pick" ? (
          <>
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3"
              style={{
                background: "rgba(127,75,255,0.18)",
                border: "1px solid rgba(127,75,255,0.45)",
              }}
            >
              <Folder size={12} color="#C9B6FF" />
              <span className="text-[11px] font-semibold tracking-wide" style={{ color: "#C9B6FF" }}>
                MOVE {count} ITEM{count === 1 ? "" : "S"}
              </span>
            </div>
            <h3 className="text-xl font-semibold tracking-tight">Move to a folder</h3>
            <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.7)" }}>
              Pick an existing folder or create a new one.
            </p>

            <button
              type="button"
              onClick={() => setStep("create")}
              className="w-full mt-4 flex items-center gap-3 px-3 py-3 rounded-xl transition hover:bg-white/5"
              style={{
                background: "linear-gradient(90deg, rgba(127,75,255,0.10), rgba(34,226,255,0.10))",
                border: "1px solid rgba(34,226,255,0.40)",
              }}
              data-testid="move-folder-new"
            >
              <span
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ background: "rgba(34,226,255,0.18)" }}
              >
                <FolderPlus size={16} color="#22E2FF" />
              </span>
              <span className="text-sm font-medium text-white">Create a new folder…</span>
            </button>

            <div className="mt-3 max-h-[40vh] overflow-y-auto" data-testid="move-folder-list">
              {visibleFolders.length === 0 ? (
                <div
                  className="text-xs px-3 py-3 rounded-xl"
                  style={{ color: "rgba(255,255,255,0.55)", background: "rgba(255,255,255,0.03)" }}
                >
                  No folders yet — create one above.
                </div>
              ) : (
                <ul className="flex flex-col gap-1.5">
                  {visibleFolders.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => doMove(s.id, null)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition hover:bg-white/5"
                        style={{
                          background: "rgba(255,255,255,0.04)",
                          border: "1px solid rgba(255,255,255,0.10)",
                        }}
                        data-testid={`move-folder-pick-${s.id}`}
                      >
                        <span
                          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                          style={{ background: "rgba(127,75,255,0.18)" }}
                        >
                          <Folder size={16} color="#C9B6FF" />
                        </span>
                        <span className="flex-1 min-w-0 text-sm font-medium truncate text-white">
                          {s.name}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Move OUT — only meaningful if any selected item already has a subfolder.
                We always show the option; the backend silently no-ops items already at root. */}
            <button
              type="button"
              disabled={busy}
              onClick={() => doMove(null, null)}
              className="w-full mt-3 text-xs py-2 rounded-full transition hover:bg-white/5"
              style={{ color: "rgba(255,255,255,0.6)" }}
              data-testid="move-folder-out"
            >
              Move out of any folder
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setStep("pick")}
              className="inline-flex items-center gap-1 text-xs mb-2 transition hover:text-white"
              style={{ color: "rgba(255,255,255,0.6)" }}
              data-testid="move-folder-back"
            >
              <ArrowLeft size={12} /> Back
            </button>
            <h3 className="text-xl font-semibold tracking-tight">New folder</h3>
            <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.7)" }}>
              Name your new folder — moves {count} item{count === 1 ? "" : "s"} into it.
            </p>
            <input
              ref={inputRef}
              className="meye-input mt-4"
              placeholder="e.g. Ceremony, Cake cutting"
              value={newName}
              onChange={(e) => setNewName(e.target.value.slice(0, 40))}
              maxLength={40}
              data-testid="move-folder-new-name"
              onKeyDown={(e) => {
                if (e.key === "Enter" && newName.trim()) doMove(null, newName.trim());
              }}
            />
            <button
              type="button"
              disabled={busy || !newName.trim()}
              onClick={() => doMove(null, newName.trim())}
              className="cta-pill w-full mt-4"
              data-testid="move-folder-create-confirm"
              style={{ opacity: !newName.trim() ? 0.5 : 1 }}
            >
              <Check size={16} />
              <span>Create &amp; move</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
